import React, { useState, useEffect } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, Space, Tag, Typography,
  Tooltip, Switch, message, Alert, Divider
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, EditOutlined, SafetyOutlined,
  InfoCircleOutlined, ReloadOutlined, CopyOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const merchantApi = axios.create({ baseURL: '/api/merchant' });
merchantApi.interceptors.request.use(config => {
  const token = localStorage.getItem('merchant_access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const MerchantSecurity = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myIP, setMyIP] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchList();
    fetchMyIP();
  }, []);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await merchantApi.get('/security/ip-whitelist');
      setList(res.data?.whitelist || []);
    } catch {
      message.error('Gagal memuat daftar IP');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyIP = async () => {
    try {
      const res = await merchantApi.get('/security/my-ip');
      setMyIP(res.data?.ip || '');
    } catch { /* silent */ }
  };

  const openAdd = () => {
    setEditRecord(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue({
      ip_address: record.ip_address,
      description: record.description || '',
      is_active: record.is_active === 1,
    });
    setModalOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        ip_address: values.ip_address,
        description: values.description || '',
        is_active: values.is_active !== false ? 1 : 0,
      };
      if (editRecord) {
        await merchantApi.put(`/security/ip-whitelist/${editRecord.id}`, payload);
        message.success('IP berhasil diupdate');
      } else {
        await merchantApi.post('/security/ip-whitelist', payload);
        message.success('IP berhasil ditambahkan');
      }
      setModalOpen(false);
      fetchList();
    } catch (err) {
      message.error(err.response?.data?.error || 'Gagal menyimpan IP');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Hapus IP dari Whitelist?',
      content: `IP ${record.ip_address} akan dihapus dari whitelist. Request dari IP ini tidak akan bisa memanggil API.`,
      okText: 'Hapus',
      okType: 'danger',
      cancelText: 'Batal',
      onOk: async () => {
        try {
          await merchantApi.delete(`/security/ip-whitelist/${record.id}`);
          message.success('IP berhasil dihapus');
          fetchList();
        } catch (err) {
          message.error(err.response?.data?.error || 'Gagal menghapus IP');
        }
      },
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => message.success('IP disalin'));
  };

  const columns = [
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      render: (v) => (
        <Space>
          <Text code>{v}</Text>
          <Tooltip title="Salin">
            <Button type="text" size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(v)} />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Keterangan',
      dataIndex: 'description',
      key: 'description',
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (v) => v ? <Tag color="green">Aktif</Tag> : <Tag color="red">Nonaktif</Tag>,
    },
    {
      title: 'Ditambahkan',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v) => new Date(v).toLocaleString('id-ID'),
    },
    {
      title: 'Aksi',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="Hapus">
            <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3}><SafetyOutlined /> Keamanan — IP Whitelist</Title>
        <Text type="secondary">
          Daftarkan IP publik server Anda agar dapat memanggil API Arendo. Request dari IP yang tidak terdaftar akan ditolak dengan kode <Text code>4010003</Text>.
        </Text>
      </div>

      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="Cara mendapatkan IP server Anda"
        description={
          <div>
            <Paragraph style={{ marginBottom: 4 }}>
              Jalankan perintah berikut dari server backend Anda:
            </Paragraph>
            <Text code>curl https://api.ipify.org</Text>
            {myIP && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <Space>
                  <Text>IP Anda saat ini:</Text>
                  <Text code strong>{myIP}</Text>
                  <Button size="small" icon={<CopyOutlined />} onClick={() => copyToClipboard(myIP)}>Salin</Button>
                  <Button
                    size="small"
                    type="primary"
                    onClick={() => {
                      form.setFieldsValue({ ip_address: myIP, description: 'IP server saya' });
                      setEditRecord(null);
                      setModalOpen(true);
                    }}
                  >
                    + Tambah IP ini
                  </Button>
                </Space>
              </>
            )}
          </div>
        }
      />

      <Card
        title={`IP Whitelist (${list.length})`}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchList}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>Tambah IP</Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={list}
          rowKey="id"
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'Belum ada IP terdaftar. Tambahkan IP server Anda untuk dapat menggunakan API.' }}
        />
      </Card>

      <Modal
        title={editRecord ? 'Edit IP Whitelist' : 'Tambah IP Whitelist'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            label="IP Address"
            name="ip_address"
            rules={[
              { required: true, message: 'IP address wajib diisi' },
              {
                pattern: /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/,
                message: 'Format IP tidak valid (contoh: 103.100.200.50)',
              },
            ]}
          >
            <Input placeholder="103.100.200.50" />
          </Form.Item>

          <Form.Item label="Keterangan (opsional)" name="description">
            <Input placeholder="Contoh: Server produksi utama" maxLength={200} />
          </Form.Item>

          {editRecord && (
            <Form.Item label="Status" name="is_active" valuePropName="checked">
              <Switch checkedChildren="Aktif" unCheckedChildren="Nonaktif" />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setModalOpen(false)}>Batal</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {editRecord ? 'Update' : 'Tambah'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default MerchantSecurity;
